import { useState, useEffect, useRef, useCallback } from 'react';
import { useCachedData } from './useCachedData';
import { supabase } from '../utils/supabase';
import { useToast } from '../context/ToastContext';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { devLog } from '../utils/devLogger';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useCareerFocus' });

export interface CareerFocus {
  id: string;
  user_id: string;
  focus_type: 'internship'; // Will expand in future
  industry_type?: string | null;
  start_date: string;
  end_date: string;
  company: string | null;
  role: string | null;
  goals: string[];
  settings: {
    defaultToFocusMode?: boolean;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CareerFocusWithMetrics extends CareerFocus {
  current_week: number;
  total_weeks: number;
  days_remaining: number;
  progress_percentage: number;
  goals?: Array<{
    id: string;
    focus_id: string;
    goal_text: string;
    goal_type: string;
    priority: number;
    evidence_target?: number;
    created_at: string;
    updated_at: string;
  }>;
}

// Helper to calculate timeline metrics
export const calculateMetrics = (focus: CareerFocus): CareerFocusWithMetrics => {
  const startDate = new Date(focus.start_date);
  const endDate = new Date(focus.end_date);
  const now = new Date();

  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
  const daysElapsed = Math.ceil((now.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
  const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

  const totalWeeks = Math.ceil(totalDays / 7);
  const currentWeek = Math.min(totalWeeks, Math.max(1, Math.ceil(daysElapsed / 7)));
  const progressPercentage = Math.min(100, Math.round((daysElapsed / totalDays) * 100));

  return {
    ...focus,
    current_week: currentWeek,
    total_weeks: totalWeeks,
    days_remaining: daysRemaining,
    progress_percentage: progressPercentage
  };
};

// Hook to get the default active career focus
// Note: This returns the DEFAULT active focus, not all active focuses
// For all active focuses, use useAllActiveFocuses hook
// When focusId is provided, returns that specific focus
// When only focusType is provided, returns the most recent focus of that type
export const useActiveCareerFocus = (focusType?: string, focusId?: string) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const lastErrorTimeRef = useRef(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Use a stable cache key that includes focus type and/or ID if specified
  const cacheKey = user?.id 
    ? focusId
      ? `${CACHE_KEYS.CAREER_FOCUS}_${focusId}` // Specific focus by ID
      : focusType 
        ? `${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}_${focusType}`
        : `${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`
    : null;

  const fetchFocus = useCallback(async () => {
      if (!user?.id) {
        // No user ID, returning null
        return null;
      }

      // If focusId is provided, fetch that specific focus
      if (focusId) {
        const { data, error } = await supabase
          .from('user_career_focuses')
          .select('*')
          .eq('id', focusId)
          .eq('user_id', user.id)
          .single();

        if (error) {
          // Check if it's a network error and handle it gracefully
          const isNetworkError = error?.message?.toLowerCase().includes('network') || 
                                error?.message?.toLowerCase().includes('internet') ||
                                error?.message?.toLowerCase().includes('fetch') ||
                                error?.code === 'NETWORK_ERROR';
          
          if (isNetworkError) {
            devLog.log('Network error while fetching career focus');
            throw new Error('Network connection unavailable');
          } else {
            devLog.error('Failed to query career focus by ID:', error);
            throw error;
          }
        }

        const result = data ? calculateMetrics(data) : null;
        setHasInitialized(true);
        return result;
      }

      // Otherwise, fetch active focus by type or most recent
      // Build query
      let query = supabase
        .from('user_career_focuses')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);
      
      // If focus type is specified, filter by it
      if (focusType) {
        query = query.eq('focus_type', focusType);
      }
      
      // Get the first active focus
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        // Check if it's a network error and handle it gracefully
        const isNetworkError = error?.message?.toLowerCase().includes('network') || 
                              error?.message?.toLowerCase().includes('internet') ||
                              error?.message?.toLowerCase().includes('fetch') ||
                              error?.code === 'NETWORK_ERROR';
        
        if (isNetworkError) {
          // For network errors, return null and let the onError handler deal with it
          devLog.log('Network error while fetching career focus');
          throw new Error('Network connection unavailable');
        } else {
          devLog.error('Failed to query active career focus:', error);
          throw error;
        }
      }

      // Query completed
      const result = data ? calculateMetrics(data) : null;
      setHasInitialized(true);
      return result;
  }, [user?.id, focusType, focusId]);

  const { data: focus, error, loading, refresh } = useCachedData<CareerFocusWithMetrics | null>(
    fetchFocus,
    {
      cacheKey: cacheKey || 'temp_career_focus',
      enabled: !!user?.id && !!cacheKey,
      skipCache: !cacheKey, // Skip cache if no valid cache key
      validateFn: (data) => {
        // Accept null or valid CareerFocus objects
        if (data === null) return true;
        if (typeof data === 'object' && data !== null && 'id' in data && 'user_id' in data) {
          return true;
        }
        return false;
      },
      onError: (error) => {
        // Check if it's a network error
        const isNetworkError = error?.message?.toLowerCase().includes('network') || 
                              error?.message?.toLowerCase().includes('internet') ||
                              error?.message?.toLowerCase().includes('fetch');
        
        if (isNetworkError) {
          devLog.log('Network connectivity issue detected, career focus data unavailable');
        } else {
          devLog.error('Failed to load active career focus:', error?.message || error);
        }
        
        setHasInitialized(true);
        
        // Only show toast for non-network errors or first network error
        const now = Date.now();
        if (now - lastErrorTimeRef.current > 5000) {
          lastErrorTimeRef.current = now;
          if (isNetworkError) {
            showToast('Unable to connect. Please check your internet connection.', 'warning');
          } else {
            showToast('Failed to load career focus', 'error');
          }
        }
      },
      onSuccess: (data) => {
        setHasInitialized(true);
      }
    }
  );

  // Clear any stale cache when user changes
  useEffect(() => {
    if (user?.id && cacheKey) {
      // Clear any old cache entries that might be using 'anon'
      const oldAnonKey = `${CACHE_KEYS.CAREER_FOCUS}_active_anon`;
      const oldCacheKey = `${CACHE_KEYS.CAREER_FOCUS}_active_${user.id}`;
      invalidateCache(oldAnonKey).catch(() => {});
      invalidateCache(oldCacheKey).catch(() => {});
    }
  }, [user?.id, cacheKey, invalidateCache]);

  // Consider loading complete when either we have data or we've initialized and confirmed no data exists
  const isFullyLoaded = loading === false && (focus !== undefined || hasInitialized);

  return { 
    focus, 
    error, 
    loading: !isFullyLoaded,
    refetch: refresh 
  };
};

// Hook to get a specific focus by ID
export const useFocusById = (focusId?: string) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const cacheKey = focusId && user?.id 
    ? `${CACHE_KEYS.CAREER_FOCUS}_${focusId}`
    : null;

  const fetchFocus = useCallback(async () => {
    if (!user?.id || !focusId) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('user_career_focuses')
        .select('*')
        .eq('id', focusId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      
      return data ? calculateMetrics(data) : null;
    } catch (err) {
      devLog.error('Failed to fetch focus by ID:', err);
      throw err;
    }
  }, [user?.id, focusId]);

  const { data: focus, error, loading, refresh } = useCachedData<CareerFocusWithMetrics | null>(
    fetchFocus,
    {
      cacheKey: cacheKey || 'temp_focus_by_id',
      enabled: !!user?.id && !!focusId && !!cacheKey,
      skipCache: !cacheKey,
      maxAge: CACHE_DURATIONS.CAREER_FOCUS,
      onError: (error) => {
        devLog.error('Failed to load focus:', error);
        showToast('Failed to load focus data', 'error');
      }
    }
  );

  return { focus, error, loading, refresh };
};

// Hook to create/activate career focus
export const useCreateCareerFocus = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [isCreating, setIsCreating] = useState(false);

  const createFocus = async (focusData: {
    focus_type: 'internship' | 'performance_review' | 'research' | 'new_job' | 'side_project' | 'academic';
    start_date: string;
    end_date: string;
    company?: string;
    role?: string;
    goals?: string[];
    settings?: Record<string, any>;
  }) => {
    if (!user?.id) {
      showToast('You must be logged in to create a career focus', 'error');
      return null;
    }

    setIsCreating(true);
    try {
      // Create new focus (removed auto-deactivation to support multiple active focuses)
      // Explicitly exclude goals to ensure they're not saved to the array field
      const { goals, ...focusDataWithoutGoals } = focusData;
      // Check if this is the user's first focus to set as default
      const { data: existingFocuses } = await supabase
        .from('user_career_focuses')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1);
      
      const isFirstFocus = !existingFocuses || existingFocuses.length === 0;
      
      const insertData = {
        user_id: user.id,
        ...focusDataWithoutGoals,
        is_active: true,
        is_default: isFirstFocus // Set as default if it's the first focus
      };
      
      const { data, error } = await supabase
        .from('user_career_focuses')
        .insert(insertData)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('Failed to create career focus');
      }

      showToast('Career focus activated successfully!', 'success');
      
      // Invalidate cache to refetch
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`);
      
      // Return the raw data without calculateMetrics in case that's causing issues
      return data[0];
    } catch (error) {
      devLog.error('Failed to create career focus:', error);
      showToast('Failed to create career focus', 'error');
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return { createFocus, isCreating };
};

// Hook to deactivate career focus
export const useDeactivateCareerFocus = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [isDeactivating, setIsDeactivating] = useState(false);

  const deactivateFocus = async (focusId: string) => {
    if (!user?.id) {
      showToast('You must be logged in', 'error');
      return false;
    }

    setIsDeactivating(true);
    try {
      const { error } = await supabase
        .from('user_career_focuses')
        .update({ is_active: false })
        .eq('id', focusId)
        .eq('user_id', user.id);

      if (error) throw error;

      showToast('Career focus deactivated', 'success');
      
      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.ALL_ACTIVE_FOCUSES}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.COMPLETED_FOCUSES}_${user.id}`);
      
      return true;
    } catch (error) {
      devLog.error('Failed to deactivate career focus:', error);
      showToast('Failed to deactivate career focus', 'error');
      return false;
    } finally {
      setIsDeactivating(false);
    }
  };

  return { deactivateFocus, isDeactivating };
};

// Hook to permanently delete career focus
export const useDeleteCareerFocus = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteFocus = async (focusId: string) => {
    logger.debug('deleteFocus called', { focusId, userId: user?.id });
    
    if (!user?.id) {
      showToast('You must be logged in', 'error');
      return false;
    }

    setIsDeleting(true);
    try {
      // Note: Due to foreign key constraints and RLS policies,
      // Supabase will automatically handle cascade deletion of related data
      // (goals, rubrics, mappings, etc.) when the focus is deleted
      
      logger.debug('Attempting to delete focus', { focusId, userId: user.id });
      
      const { data, error } = await supabase
        .from('user_career_focuses')
        .delete()
        .eq('id', focusId)
        .eq('user_id', user.id)
        .select(); // Add select to see what was deleted

      logger.debug('Delete result', { data, error });

      if (error) {
        logger.error('Delete error details', error);
        throw error;
      }

      // Check if anything was actually deleted
      if (!data || data.length === 0) {
        logger.warn('No focus was deleted - it may not exist or user may not have permission');
        showToast('Focus not found or you do not have permission to delete it', 'error');
        return false;
      }

      showToast('Career focus deleted permanently', 'success');
      
      // Invalidate all relevant caches
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.ALL_ACTIVE_FOCUSES}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.COMPLETED_FOCUSES}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS}_${focusId}`);
      
      return true;
    } catch (error) {
      logger.error('Failed to delete career focus', error);
      showToast('Failed to delete career focus', 'error');
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteFocus, isDeleting };
};

// Utility functions
export const isWithinFocusDates = (focus: CareerFocus): boolean => {
  const now = new Date();
  const startDate = new Date(focus.start_date);
  const endDate = new Date(focus.end_date);
  
  return now >= startDate && now <= endDate;
};

export const formatFocusDateRange = (focus: CareerFocus): string => {
  const startDate = new Date(focus.start_date);
  const endDate = new Date(focus.end_date);
  
  const formatter = new Intl.DateTimeFormat('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
  
  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
};